import React, { useState, useEffect } from 'react';
import { apiClient } from '../../utils/api';
import { RefreshCw, Copy, Check, ChevronDown, ChevronUp } from 'lucide-react';

interface PromptCraftRecord {
  id: string;
  user_id: string;
  seed_prompt: string;
  style_preference: string | null;
  aspect_ratio: string;
  expanded_results: string;
  created_at: string;
}

interface StructuredFields {
  subject?: {
    core_noun?: string;
    age_status_identity?: string;
    appearance?: string;
    clothing_accessories?: string;
    expression?: string;
    pose?: string;
    material_hint?: string;
  };
  action?: string;
  environment?: string;
  style?: string;
  lighting?: string;
  camera?: string;
  mood_material?: string;
}

interface ParsedResults {
  full_prompt?: string;
  structured?: StructuredFields;
  type?: 'generate' | 'decompose';
}

export const PromptCraftTab: React.FC = () => {
  const [records, setRecords] = useState<PromptCraftRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    fetchHistory();
  }, [page]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(
        `/api/admin/promptcraft-history?page=${page}&limit=20`
      );
      setRecords(response.data.records || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch promptcraft history:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const parseResults = (raw: string): ParsedResults => {
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  };

  const subjectLabels: Record<string, string> = {
    core_noun: '核心名词',
    age_status_identity: '年龄/身份',
    appearance: '外貌',
    clothing_accessories: '服装配饰',
    expression: '表情',
    pose: '姿势',
    material_hint: '材质提示',
  };

  const topLevelLabels: Record<string, string> = {
    action: '动作',
    environment: '环境',
    style: '风格',
    lighting: '光线',
    camera: '镜头',
    mood_material: '氛围/材质',
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">PromptCraft 历史</h2>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-end mb-6">
          <button
            onClick={fetchHistory}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>刷新</span>
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">加载中...</p>
          </div>
        ) : records.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>暂无 PromptCraft 记录</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {['类型', '种子提示词', '风格偏好', '比例', '创建时间', ''].map((h) => (
                      <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {records.map((record) => {
                    const isExpanded = expandedRows.has(record.id);
                    const parsed = parseResults(record.expanded_results);
                    const recordType = parsed.type || 'generate';
                    return (
                      <React.Fragment key={record.id}>
                        <tr
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => toggleRow(record.id)}
                        >
                          <td className="px-6 py-4 text-sm whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              recordType === 'decompose'
                                ? 'bg-purple-100 text-purple-800'
                                : 'bg-cyan-100 text-cyan-800'
                            }`}>
                              {recordType === 'decompose' ? '拆解' : '生成'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                            <div className="truncate" title={record.seed_prompt}>
                              {record.seed_prompt.slice(0, 40)}{record.seed_prompt.length > 40 ? '…' : ''}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {record.style_preference || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                            {record.aspect_ratio}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                            {new Date(record.created_at).toLocaleString('zh-CN')}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-400">
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </td>
                        </tr>
                        {isExpanded && parsed && (
                          <tr>
                            <td colSpan={6} className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                              <div className="space-y-4">
                                {/* Full prompt */}
                                <div>
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs font-semibold text-gray-500 uppercase">完整提示词</span>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleCopy(parsed.full_prompt || '', record.id); }}
                                      className="flex items-center space-x-1 text-xs text-blue-600 hover:text-blue-800"
                                    >
                                      {copiedId === record.id
                                        ? <><Check className="w-3 h-3" /><span>已复制</span></>
                                        : <><Copy className="w-3 h-3" /><span>复制</span></>}
                                    </button>
                                  </div>
                                  <p className="text-sm text-gray-800 bg-white border border-gray-200 rounded p-3 whitespace-pre-wrap">
                                    {parsed.full_prompt || '-'}
                                  </p>
                                </div>
                                {/* Structured fields */}
                                {parsed.structured && (
                                  <div>
                                    <span className="text-xs font-semibold text-gray-500 uppercase block mb-2">结构化字段</span>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                      {parsed.structured.subject && Object.entries(parsed.structured.subject).map(([k, v]) => (
                                        v ? (
                                          <div key={k} className="bg-white border border-gray-200 rounded p-2">
                                            <div className="text-xs text-gray-400">{subjectLabels[k] || k}</div>
                                            <div className="text-sm text-gray-800 mt-0.5">{v}</div>
                                          </div>
                                        ) : null
                                      ))}
                                      {Object.entries(topLevelLabels).map(([k, label]) => {
                                        const v = (parsed.structured as Record<string, unknown>)[k] as string | undefined;
                                        return v ? (
                                          <div key={k} className="bg-white border border-gray-200 rounded p-2">
                                            <div className="text-xs text-gray-400">{label}</div>
                                            <div className="text-sm text-gray-800 mt-0.5">{v}</div>
                                          </div>
                                        ) : null;
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">共 {total} 条记录</div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 text-gray-700"
                >
                  上一页
                </button>
                <span className="text-sm text-gray-600">第 {page} 页</span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={records.length < 20}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 text-gray-700"
                >
                  下一页
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
